import { createContext, useContext, type ReactNode } from 'react';
import type { Session, SoundEngineer } from '@/api/client';
import type { GridSelection } from '@/schedule/scheduleTypes';

export type SessionUpdatePatch = Parameters<typeof import('@/api/client').api.sessions.update>[1];

/** Стабильные хендлеры + данные. Никогда не меняются между рендерами.
 * SessionBubble подписывается только сюда → 0 лишних ре-рендеров при изменении выделения */
export interface ScheduleHandlersContextValue {
  soundengineers: SoundEngineer[];
  /** addToSelection — клик с модификатором выделения (Ctrl, на Mac ⌘), см. hasSlotSelectModifier */
  onSlotClick: (date: string, studioIdx: number, slotIdx: number, addToSelection: boolean) => void;
  onSlotDrop: (date: string, studioIdx: number, slotIdx: number, e: React.DragEvent) => void;
  onSessionDropOnSession: (draggedSessionId: string, targetSession: Session, copy: boolean) => void;
  onAddClick: () => void;
  onSessionEdit: (session: Session) => void;
  onSessionClick: () => void;
  onSessionDelete: (sessionId: string) => void;
  onSessionResize: (session: Session, newStartMins: number, newEndMins: number) => void;
  onSessionUpdate: (sessionId: string, patch: SessionUpdatePatch) => Promise<void>;
  onSessionOptimisticUpdate: (sessionId: string, patch: SessionUpdatePatch) => void;
  /** Восстановить отменённую сессию с подбором слота */
  onRestoreCanceledSession: (session: Session) => Promise<void>;
  /** Открыть попап отменённых; повторный клик по той же кнопке — закрытие с exit-анимацией */
  onOpenCanceledSessionsPopover: (dateIso: string, anchorEl: HTMLElement) => void;
}

/** Volatile-состояние выделения слота. Меняется при каждом клике по ячейке.
 * Только DayCell подписывается сюда → ре-рендерятся только 42 ячейки, а не 500 пузырьков */
export interface ScheduleSelectionContextValue {
  gridSelection: GridSelection | null;
}

const ScheduleHandlersContext = createContext<ScheduleHandlersContextValue | null>(null);
const ScheduleSelectionContext = createContext<ScheduleSelectionContextValue | null>(null);

interface ScheduleProviderProps {
  handlers: ScheduleHandlersContextValue;
  selection: ScheduleSelectionContextValue;
  children: ReactNode;
}

export function ScheduleProvider({ handlers, selection, children }: ScheduleProviderProps) {
  return (
    <ScheduleHandlersContext.Provider value={handlers}>
      <ScheduleSelectionContext.Provider value={selection}>
        {children}
      </ScheduleSelectionContext.Provider>
    </ScheduleHandlersContext.Provider>
  );
}

export function useScheduleHandlers(): ScheduleHandlersContextValue {
  const ctx = useContext(ScheduleHandlersContext);
  if (!ctx) throw new Error('useScheduleHandlers must be used within ScheduleProvider');
  return ctx;
}

export function useScheduleSelection(): ScheduleSelectionContextValue {
  const ctx = useContext(ScheduleSelectionContext);
  if (!ctx) throw new Error('useScheduleSelection must be used within ScheduleProvider');
  return ctx;
}
